import { motion } from "framer-motion";
import { ArrowRight, PenTool, CheckCircle, BookOpen, HardHat, CircleDollarSign, Key } from "lucide-react";
import PageHero from "../components/PageHero";
import MagneticButton from "../components/MagneticButton";
import SectionHeading from "../components/SectionHeading";
import { services } from "../data/site";
import useDynamicContent from "../hooks/useDynamicContent";

const newServices = [
  {
    id: "A",
    title: "Project Design",
    desc: "Comprehensive architectural and structural design solutions that balance aesthetics with regulatory compliance. We create blueprints that are optimized for AutoDCR and swift ULB approvals from day one.",
    icon: PenTool,
    color: "from-blue-500/20 to-blue-900/5"
  },
  {
    id: "B",
    title: "Project Approvals",
    desc: "End-to-end liaison across GHMC, HMDA, Fire, AAI, MoEF, and TGIIC. We take full accountability for navigating through bureaucracy, resolving queries, and securing your sanctions without delays.",
    icon: CheckCircle,
    color: "from-amber-500/20 to-amber-900/5"
  },
  {
    id: "C",
    title: "Project Brochure for Sales & Strategic Planning",
    desc: "Transforming approved plans into compelling marketing collateral. We develop strategic sales brochures, 3D renderings, and project positioning materials to help you launch into the market with impact.",
    icon: BookOpen,
    color: "from-emerald-500/20 to-emerald-900/5"
  },
  {
    id: "D",
    title: "Project Execution",
    desc: "Turning blueprints into reality with rigorous project management. We oversee construction quality, ensure adherence to the approved plans, and maintain strict timelines on the ground.",
    icon: HardHat,
    color: "from-purple-500/20 to-purple-900/5"
  },
  {
    id: "E",
    title: "Project Funding",
    desc: "Strategic financial structuring and funding assistance. We help connect viable projects with institutional lenders, private equity, and structured finance partners based on robust approval documentation.",
    icon: CircleDollarSign,
    color: "from-green-500/20 to-green-900/5"
  },
  {
    id: "F",
    title: "Project Handover with Occupancy Certificate from the Urban Local Body",
    desc: "The final, crucial milestone. We handle all final inspections, compliance checks, and secure the Occupancy Certificate (OC) from the Urban Local Body, ensuring a smooth handover to buyers.",
    icon: Key,
    color: "from-rose-500/20 to-rose-900/5"
  }
];

export default function ServicesPage() {
  const { content } = useDynamicContent('services');
  
  const heroEyebrow = content.hero_eyebrow || "Our Critical Services";
  const heroTitle = content.hero_title || "A comprehensive 6-pillar approach from concept to keys.";
  const heroDesc = content.hero_description || "We don't just get your drawings stamped. We partner with you through the entire lifecycle of your real estate project — from the first architectural sketch to project loans, construction execution, and sales.";
  
  const ctaTitle = content.cta_title || "Let's structure your next project for success.";
  const ctaDesc = content.cta_description || "Speak to our experts about combining design, approvals, funding, and execution into one seamless pipeline.";

  return (
    <main data-testid="services-page" className="bg-[#0a0a0c]">
      <PageHero
        eyebrow={heroEyebrow}
        title={heroTitle}
        description={heroDesc}
        image="https://images.unsplash.com/photo-1769556353286-b7f50da70d0c?crop=entropy&cs=srgb&fm=jpg&w=2200&q=85"
        breadcrumbs={["Home", "Services"]}
      />

      <section className="relative py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto space-y-12 md:space-y-24">
           {newServices.map((service, idx) => {
             const isEven = idx % 2 === 0;
             return (
               <motion.div 
                 key={service.id}
                 initial={{ opacity: 0, y: 40 }}
                 whileInView={{ opacity: 1, y: 0 }}
                 viewport={{ once: true, amount: 0.2 }}
                 transition={{ duration: 0.7, ease: "easeOut" }}
                 className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-10 md:gap-20 items-center`}
               >
                 {/* Text Content */}
                 <div className="flex-1 space-y-6">
                    <div className="flex items-center gap-4 mb-4">
                       <span className="flex items-center justify-center w-12 h-12 rounded-full border border-white/10 bg-white/5 font-display text-xl text-amber-500">
                          {service.id}
                       </span>
                       <div className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent"></div>
                    </div>
                    <h2 className="text-3xl md:text-5xl font-display leading-tight">{service.title}</h2>
                    <p className="text-lg text-white/60 leading-relaxed max-w-xl">
                      {service.desc}
                    </p>
                    <div className="pt-6">
                       <service.icon size={48} strokeWidth={1} className="text-white/20" />
                    </div>
                 </div>

                 {/* Visual Block */}
                 <div className="flex-1 w-full aspect-[4/3] md:aspect-square max-w-md mx-auto relative rounded-3xl overflow-hidden border border-white/10 group">
                    <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-40 group-hover:opacity-60 transition-opacity duration-700`}></div>
                    <div className="absolute inset-0 bg-noise opacity-30 mix-blend-overlay"></div>
                    <div className="absolute inset-0 flex items-center justify-center p-12">
                       <div className="w-full h-full rounded-2xl border border-white/5 bg-black/40 backdrop-blur-sm flex items-center justify-center relative overflow-hidden">
                          {/* Animated decorative element */}
                          <motion.div 
                            initial={{ scale: 0.8, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 1, delay: 0.2 }}
                            viewport={{ once: true }}
                            className="absolute inset-0 flex items-center justify-center"
                          >
                            <service.icon size={120} strokeWidth={0.5} className="text-white/10" />
                          </motion.div>
                       </div>
                    </div>
                 </div>
               </motion.div>
             )
           })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 bg-neutral-900/50 border-t border-white/10">
        <div className="max-w-4xl mx-auto flex flex-col items-center text-center">
           <SectionHeading 
             eyebrow="Ready to build?" 
             title={ctaTitle} 
             description={ctaDesc} 
             centered 
           />
           <div className="mt-10 flex justify-center pt-8">
              <MagneticButton href="/contact" className="!py-4 !px-8 text-lg">
                 Contact Our Team <ArrowRight size={20} />
              </MagneticButton>
           </div>
        </div>
      </section>
    </main>
  );
}
